/**
 * Planning V1 API client (SOLY-PLAN-001). Typed wrappers over /api/planning/* and
 * /api/admin/employees. Mirrors the backend DTOs — keep in sync with the C# contracts.
 */
import { apiClient } from '../client'

// ── Shared enums (string unions matching the BE enum names) ─────────────────
/**
 * SOLY-PLAN-002: `InternalWork` (« Interne ») splits internal work — montage atelier, divers, admin,
 * réunion — out of `Appointment`. It has no counterparty, so it never carries a confirmation.
 */
export type ScheduleEventKind = 'TechnicalSurvey' | 'Installation' | 'Appointment' | 'InternalWork'
/** Round 3 (PR #616 comment 2115): durée canonique. Hours = même journée (step 0.5h) ; Days = N jours pleins. */
export type DurationUnit = 'Hours' | 'Days'
// Lifecycle only (mini-scope 28). « Planifié ? » is derived from currentEventId, not stored here —
// NotScheduled/Scheduled collapsed into Open.
export type WorkItemStatus =
  | 'Open' | 'InProgress' | 'Completed' | 'NotApplicable' | 'Cancelled'
export type EmployeeSkillType = 'Commercial' | 'Surveyor' | 'Installer'
/**
 * SOLY-FINITION-001 — why this pose exists. A LABEL only (badge + agenda tint + KPI): alert rules
 * stay uniform. Never user-chosen: the Phase-1 pose is Initial, every later one is a Finition.
 * SOLY-PLAN-003 dropped 'Sav' (a return after the final invoice) — the BE no longer sends it.
 */
export type InterventionNature = 'Initial' | 'Finition'

/**
 * SOLY-REGIE-001 — how a work item is executed, independently of how it was sold and of why the
 * file exists. FR labels: « Pose » (« Placement » once lot 3 aligns on the trade word) and
 * « Intervention ».
 */
export type WorkType = 'Installation' | 'Intervention'
export type SupplierOrderStatus = 'NotOrdered' | 'Ordered' | 'Confirmed' | 'Received'
export type AlertSeverity = 'Soft' | 'Hard'
export type AlertAnchorStep = 'Commandes' | 'Metre' | 'Pose' | 'Rapport'
/**
 * How a slot was confirmed (BE enum). SOLY-PLAN-002 renamed the TYPE — the confirming party is not
 * necessarily the client — but deliberately NOT the values: the channel keeps its exact meaning.
 */
export type ConfirmationChannel = 'Phone' | 'Email' | 'Sms' | 'Other'

// ── DTOs ────────────────────────────────────────────────────────────────────
export interface ScheduleEventAssignmentDto {
  assignedUserId: string
  assignedAt: string
}

export interface ScheduleEventDto {
  id: string
  organizationId: string
  kind: ScheduleEventKind
  technicalSurveyId: string | null
  installationId: string | null
  projectId: string | null
  durationUnit: DurationUnit
  durationValue: number
  /**
   * SOLY-PLAN-007 — the calendar start, zone-less: the day typed (« yyyy-MM-dd ») and the wall clock
   * (« HH:mm »), null on a Days slot. A date compares as a string; nothing here goes through a
   * `Date` or an offset.
   */
  startDate: string
  startTime: string | null
  /** The LAST day the slot occupies, INCLUSIVE — `startDate` for Hours, startDate + N − 1 for N Days. */
  endDate: string
  /** @deprecated Legacy instant, still served until SOLY-PLAN-007 MS-06. Read the calendar fields. */
  startDateTime: string
  /** @deprecated Legacy exclusive end instant, still served until SOLY-PLAN-007 MS-06. Read `endDate`. */
  endDateTime: string
  cancelledAt: string | null
  /**
   * Confirmation of THIS slot — « Proposé » while null, « Confirmé » once set. Voided by a
   * reschedule to another start. Always null on an InternalWork: it has no counterparty.
   */
  confirmedAt: string | null
  confirmationChannel: ConfirmationChannel | null
  confirmationNote: string | null
  assignments: ScheduleEventAssignmentDto[]
  /** Agenda display enrichment (client + job-site city) — null on mutation responses. */
  clientName: string | null
  city: string | null
  postalCode: string | null
  /**
   * Free text carried by the slot. On a RDV or internal work it is also the bar's TITLE (« Rencontrer
   * M. Dupont »); on a pose or a métré it is the note of that créneau (SOLY-PLAN-004), the chantier
   * keeping the title. One column, two readings, decided by the kind — see kindHasFreeLabel.
   */
  label: string | null
  /**
   * Nature of the linked pose — null for métré/RDV events (SOLY-FINITION-001).
   * ⚠️ SOLY-REGIE-001: superseded by `workType` for the tint. Still served (and still read as a
   * FALLBACK) until the backend everywhere carries the work type — a deployment window must never
   * leave the agenda with neither. Dropped with the column in lot 3.
   */
  installationNature?: InterventionNature | null
  /**
   * How the linked work item is executed (SOLY-REGIE-001) — null for métré/RDV events.
   * THE tint discriminator: an Intervention (retouche on a new file, or finition on a file already
   * sold) is amber, a placement is blue.
   */
  workType?: WorkType | null
  /**
   * SOLY-PLAN-003 MS-08 — where this créneau sits among the pose's live créneaux (« 2/3 »).
   * Counted server-side over ALL the pose's slots, not just the ones in the loaded agenda window:
   * a marker that reads « 1/2 » and becomes « 1/3 » after scrolling would be worse than none.
   * 0/0 for métré, RDV and internal work.
   */
  slotOrdinal?: number
  slotCount?: number
  /**
   * Lifecycle of the work item behind this slot — null on a RDV or internal work, which have none.
   *
   * THE end of the « Proposé / Confirmé » question: it dies once the work item leaves
   * Open / InProgress. Deliberately not `endDateTime`: a RDV the client never confirmed is a RDV
   * that did not happen, and the day after it the secretary still has to confirm it or move it — a
   * bar that stopped asking would be telling her it took place.
   *
   * Optional: served by a backend that may be one deployment behind, in which case every slot reads
   * as « still open » and the agenda behaves exactly as it did before.
   */
  workItemStatus?: WorkItemStatus | null
  /**
   * When that work item was closed by a report — the date behind « Clôturé le … ». A LABEL, never
   * the predicate: a cancelled work item is just as done and carries no instant at all.
   */
  workItemClosedAt?: string | null
  /**
   * SOLY-PLAN-008 — the linked project's reference, read under the client on the agenda's
   * « Projets » lanes. Null on a free appointment; absent until the backend serving it is deployed.
   */
  projectReference?: string | null
}

/**
 * Inbox V1 is pose-driven: a row exists iff the project still has a pose to schedule. The card opens
 * the project (no quick-schedule button) and shows `requestedInstallDate` — the key the server sorts
 * this list by.
 */
export interface InboxProjectRowDto {
  projectId: string
  clientName: string
  signedAt: string | null
  requestedInstallDate: string | null
  reference: string | null
  projectType: string | null
  city: string | null
  postalCode: string | null
  poseCreatedAt: string | null
  /** Nature of the work item that put the project here (SOLY-FINITION-001) — drives the badge. */
  nature: InterventionNature
  /** Phase of that work item. The server already sorts interventions below Phase-1 poses. */
  phase: number
  /**
   * SOLY-PLAN-003 MS-05 — hours still to place on that pose. > 0 with `plannedSlots` > 0 means
   * « partiellement planifiée »: some créneaux are placed, the budget is not spent. Raw hours on
   * purpose; render them through `estimateUnit` so « il reste 2 j » and « il reste 4 h » come from
   * one conversion, not two.
   */
  remainingHours: number
  /** Unit the estimate was written in (`Days` | `Hours`) — how the remainder should read. */
  estimateUnit: DurationUnit
  /** Live créneaux already placed on that pose. 0 = nothing planned yet (the historical case). */
  plannedSlots: number
  /**
   * SOLY-REGIE-001 — how the work executes. The card names ITSELF from this: without it the label
   * falls back to the literal word « Chantier » on a file whose entire point is not being one.
   */
  workType?: WorkType | null
}

/**
 * The org's reference work day (`GET /api/planning/settings`). The frontend holds NO copy of these
 * numbers — see planning-settings.ts. `hoursPerWorkDay` is not the span between the two hours: the
 * day is 7h→16h with an hour of break, so it is worth 8 worked hours.
 */
export interface PlanningSettingsDto {
  workDayStartHour: number
  workDayEndHour: number
  hoursPerWorkDay: number
}

export interface SuiviAlertDto {
  code: number
  key: string
  severity: AlertSeverity
  anchorStep: AlertAnchorStep
  /** The agenda bar carrying the badge — route on this, never on code numbers (_constraints.md). */
  workItem: ConstraintWorkItemKind
  title: string
  context: string
  /** The date the alert races against (event/pose date, « yyyy-MM-dd »); null for after-the-fact alerts. */
  dueDate: string | null
}

export interface SuiviProjectGroupDto {
  projectId: string
  clientName: string
  reference: string | null
  /** Day of the next upcoming slot (rule D8), « yyyy-MM-dd ». */
  nextEventAt: string | null
  alerts: SuiviAlertDto[]
}

export interface SuiviDto {
  projectCount: number
  pointCount: number
  projects: SuiviProjectGroupDto[]
}

// ── SOLY-PO-001 — supplier orders (MS-01/02, V102 line-level delivery) ────────

/** V102: Confirmed added (supplier confirmed the period); PreOrderSent + WaitingParty removed. */
export type SupplierLineStatus = 'ToOrder' | 'OnHold' | 'Assigned' | 'Confirmed' | 'Received' | 'Cancelled'
export type SupplierOrderState = 'Draft' | 'Sent' | 'Confirmed' | 'Closed'
export type SupplierLineHoldKind = 'Grouping' | 'Info'
export type SupplierOrderMode = 'Email' | 'Portal' | 'Tradeplace' | 'Phone'
export type PeriodGranularity = 'Day' | 'Week'

export interface SupplierDto {
  id: string
  name: string
  allowedModes: SupplierOrderMode[]
  expectsConfirmation: boolean
  defaultConfirmationDelayDays: number | null
}

/** A delivery period: one day (start==end) or one ISO week (Mon→Sun). Dates are yyyy-MM-dd. */
export interface PeriodDto {
  start: string
  end: string
  granularity: PeriodGranularity
}

export interface SupplierOrderLineDto {
  id: string
  /** Soft ref to the client order line this tracks (null = added by hand). */
  sourceOrderLineId: string | null
  supplierName: string | null
  /** Short product name (SOLY-PO-001 UX). Null for lines created before V108 — fallback to description. */
  title: string | null
  description: string
  /** Quantity to order (MS-14) — carried from the client order line or typed on a free line; null when unknown. */
  quantity: number | null
  // MS-02 — line state machine
  supplierId: string | null
  supplierOrderId: string | null
  lineStatus: SupplierLineStatus
  holdKind: SupplierLineHoldKind | null
  holdUntil: string | null
  holdNote: string | null
  cancelledDate: string | null
  cancelReason: string | null
  receivedDate: string | null
  // V102 — line-level delivery tracking (moved from SupplierOrder)
  desiredDeliveryPeriod: PeriodDto | null
  confirmedDeliveryPeriod: PeriodDto | null
  confirmationExpectedBy: string | null
  confirmedAt: string | null
}

/**
 * An order referenced by this project's lines — ORG-LEVEL, counts are org-wide (grouped orders).
 * V102: period fields moved to lines; PreOrderSentDate, WaitingOn, InfoDueDate, ConfirmationExpectedBy removed.
 */
export interface SupplierOrderDto {
  id: string
  supplierId: string
  supplierName: string
  status: SupplierOrderState
  mode: SupplierOrderMode | null
  sentDate: string | null
  supplierOrderNumber: string | null
  totalLineCount: number
  totalProjectCount: number
}

// ── SOLY-PO-001 MS-16 — org-wide « Commandes fournisseurs » read-only registre ──
// The BE aggregates delivery span + next deadline from the lines (V102); the page never recomputes.

export interface BoardDeadlineDto {
  date: string
  kind: 'confirmation' | 'livraison'
}

export interface BoardProjectRefDto {
  projectId: string
  clientName: string
}

export interface BoardOrderLineDto {
  id: string
  projectId: string
  clientName: string
  /** The line read as one string: « Titre — Détail », with a legacy folded title unfolded once. */
  label: string
  lineStatus: SupplierLineStatus
  deliveryStart: string | null
  deliveryEnd: string | null
  deliveryConfirmed: boolean
}

/** A row of the org-wide read-only registre — one non-closed order, aggregated over its lines. */
export interface BoardOrderDto {
  id: string
  supplierId: string
  supplierName: string
  status: SupplierOrderState
  mode: SupplierOrderMode | null
  sentDate: string | null
  supplierOrderNumber: string | null
  deliveryStart: string | null
  deliveryEnd: string | null
  deliveryConfirmed: boolean
  nextDeadline: BoardDeadlineDto | null
  totalLineCount: number
  receivedLineCount: number
  projects: BoardProjectRefDto[]
  lines: BoardOrderLineDto[]
}

/** Derived readiness for a work item (BE-computed, MS-23). `missing` ⊆ known input keys. */
export interface ReadyCheckDto {
  ready: boolean
  missing: string[]
}

export interface WorkItemDto {
  id: string
  phase: number
  status: WorkItemStatus
  /** The live RDV ref (mini-scope 28) — null = « à planifier », set = « planifié ». Source of truth. */
  currentEventId: string | null
  reportSummary: string | null
  reportSummaryAt: string | null
  notApplicableReason: string | null
  cancelReason: string | null
  readyCheck: ReadyCheckDto
}

export interface SurveyDto extends WorkItemDto {
  estimatedDurationHours: number
  /** missing ⊆ {"schedule","surveyor","estimate","clientConfirmation"} */
}

export interface InstallationDto extends WorkItemDto {
  estimatedDurationUnit: DurationUnit
  /**
   * Estimated duration and crew — `null` until somebody actually says (SOLY-PLAN-004). A pose used
   * to be born at 1 j / 2 placeurs, figures nobody had typed and that the cockpit read as a
   * decision. The UNIT stays non-null: it is the position of the Jours/Heures toggle, not part of
   * the estimate.
   */
  estimatedDurationValue: number | null
  estimatedResourceCount: number | null
  schedulingNotes: string | null
  /** missing ⊆ {"survey","supplierOrders","installer","clientConfirmation"} */
  /** Why this pose exists (SOLY-FINITION-001) — 'Initial' for the Phase-1 pose. */
  nature: InterventionNature
}

/** Alert joined onto the project detail (filtered to this project by the AlertEngine). */
export interface ProjectDetailAlertDto {
  code: number
  key: string
  severity: AlertSeverity
  anchorStep: AlertAnchorStep
  /** The agenda bar carrying the badge — same narration contract as SuiviAlertDto. */
  workItem: ConstraintWorkItemKind
  title: string
  context: string
  /** « yyyy-MM-dd ». */
  dueDate: string | null
}

/** Full job-site address for the cockpit detail header (BE PlanningSiteAddress). */
export interface PlanningSiteAddressDto {
  street: string
  number: string
  box: string | null
  floor: string | null
  postalCode: string
  city: string
}

/** A client contact for the cockpit detail header (BE PlanningContact). */
export interface PlanningContactDto {
  name: string
  phone: string | null
  email: string | null
  isPrimary: boolean
}

export interface ProjectDetailDto {
  projectId: string
  clientName: string
  clientId: string | null
  reference: string | null
  signedAt: string | null
  requestedInstallDate: string | null
  projectName: string
  siteAddress: PlanningSiteAddressDto | null
  contacts: PlanningContactDto[]
  salesOwnerName: string | null
  technicalSurveys: SurveyDto[]
  installations: InstallationDto[]
  events: ScheduleEventDto[]
  supplierOrderLines: SupplierOrderLineDto[]
  supplierOrders: SupplierOrderDto[]
  alerts: ProjectDetailAlertDto[]
  /** The explicit « rien à commander » marker (MS-03) — the C1 exit, dated. */
  nothingToOrderAt: string | null
}

export interface EmployeeDto {
  userId: string
  email: string
  displayName: string
  skills: EmployeeSkillType[]
  /**
   * SOLY-TEAM-001: false = soft-deactivated. The roster keeps them so past
   * events resolve their name; assignable lists and agenda lanes filter on it.
   */
  isActive: boolean
  /**
   * SOLY-PLAN-005 — the crew this member belongs to, or null. Served on the roster rather than by a
   * dedicated endpoint because the agenda already loads it to build its lanes: grouping then costs
   * no extra request, no loading state and no cache. Null means « own lane ».
   *
   * OPTIONAL, not just nullable: a backend that predates SOLY-PLAN-005 sends neither field, and the
   * honest reading of a missing crew is the same as the reading of no crew — one lane per person,
   * exactly what the agenda did before. Nothing to guard, nothing to break.
   */
  crewId?: string | null
  crewName?: string | null
}

export interface UnavailabilityDto {
  id: string
  userId: string
  label: string
  durationUnit: DurationUnit
  durationValue: number
  /** Same calendar fields as {@link ScheduleEventDto} — day, wall clock (null for Days), inclusive last day. */
  startDate: string
  startTime: string | null
  endDate: string
  /** @deprecated Legacy instant, still served until SOLY-PLAN-007 MS-06. */
  startDateTime: string
  /** @deprecated Legacy exclusive end instant, still served until SOLY-PLAN-007 MS-06. */
  endDateTime: string
}

/**
 * A project found by the planning search (BE PlanningProjectSearchRow): accepted + in execution
 * (≥1 métré/pose). `lastPlannedEventStart` is the start DAY (« yyyy-MM-dd ») of its latest live RDV,
 * or null when nothing is scheduled yet — the agenda jumps to that week on open (Elodie 2026-06-14).
 */
export interface PlanningProjectSearchRowDto {
  projectId: string
  clientName: string
  reference: string | null
  lastPlannedEventStart: string | null
}

// ── Calendar fields fallback (SOLY-PLAN-007 MS-03, removed in MS-06) ────────
type CalendarSlot = Pick<ScheduleEventDto, 'durationUnit' | 'durationValue' | 'startDate' | 'startTime' | 'endDate' | 'startDateTime'>

/**
 * Fills `startDate` / `startTime` / `endDate` from the legacy instant when a response lacks them.
 * The ONLY place the legacy instant is read; every screen reads the calendar fields.
 *
 * ⚠️ It covers READS only. A backend that does not serve these fields does not accept them in
 * commands either (both arrive with SOLY-PLAN-007 R1): this frontend must never reach an
 * environment before the R1 backend — a Days slot written without `startDateTime` would land on
 * 0001-01-01. The deployment order is a gate (BE R1, then R2, then this frontend), not something
 * this fallback makes safe.
 *
 * Read off the string as written, never through `new Date()`: a Days slot is anchored at UTC
 * midnight and an Hours slot carries its wall clock in its own offset, so the characters ARE the
 * day and the hour somebody typed. A response that already carries `startDate` is returned as is.
 *
 * Removed in SOLY-PLAN-007 MS-06, once no backend without the calendar fields can answer.
 */
export function withCalendarFields<T extends CalendarSlot>(slot: T): T {
  if (slot.startDate) return slot
  const startDate = slot.startDateTime.slice(0, 10)
  const isHours = slot.durationUnit === 'Hours'
  let endDate = startDate
  if (!isHours) {
    const end = new Date(`${startDate}T00:00:00Z`)
    end.setUTCDate(end.getUTCDate() + Math.max(1, Math.round(slot.durationValue)) - 1)
    endDate = end.toISOString().slice(0, 10)
  }
  return { ...slot, startDate, startTime: isHours ? slot.startDateTime.slice(11, 16) : null, endDate }
}

const withCalendarFieldsAll = <T extends CalendarSlot>(slots: T[]): T[] => slots.map(withCalendarFields)

// ── Agenda / strip / inbox / suivi ──────────────────────────────────────────
/**
 * `from` / `to` are calendar days (« yyyy-MM-dd »): `from` is the first day shown, `to` the day
 * AFTER the last one — exclusive (SOLY-PLAN-007). Same contract for density and unavailabilities.
 */
export const getEvents = (from: string, to: string) =>
  apiClient.get<ScheduleEventDto[]>(`/api/planning/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
    .then(withCalendarFieldsAll)

export const getDensity = (from: string, to: string) =>
  apiClient.get<Record<string, number>>(`/api/planning/density?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)

export const getInbox = () => apiClient.get<InboxProjectRowDto[]>('/api/planning/inbox')

export const searchPlanningProjects = (q: string) =>
  apiClient.get<PlanningProjectSearchRowDto[]>(`/api/planning/projects/search?q=${encodeURIComponent(q)}`)

/**
 * SOLY-PLAN-008 — appointments and internal work whose title contains `q`, upcoming first then the
 * recent past. Same enriched shape as the agenda, so a pick opens the fiche with no second call.
 */
export const searchPlanningEvents = (q: string) =>
  apiClient.get<ScheduleEventDto[]>(`/api/planning/events/search?q=${encodeURIComponent(q)}`)

export const getSuivi = () => apiClient.get<SuiviDto>('/api/planning/suivi')

export const getHolidays = (year: number) => apiClient.get<string[]>(`/api/planning/holidays?year=${year}`)

export const getPlanningSettings = () => apiClient.get<PlanningSettingsDto>('/api/planning/settings')

export const getUnavailabilities = (from: string, to: string) =>
  apiClient.get<UnavailabilityDto[]>(`/api/planning/unavailabilities?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
    .then(withCalendarFieldsAll)

/**
 * The start a command sends for a slot or an absence (SOLY-PLAN-007): the calendar day, plus the
 * wall clock « HH:mm » for an hourly one — null for Days. No instant, no offset.
 */
export interface SlotStartBody {
  startDate: string
  startTime: string | null
}

export const createUnavailability = (body: { userId: string; label: string; durationUnit: DurationUnit; durationValue: number } & SlotStartBody) =>
  apiClient.post<UnavailabilityDto>('/api/planning/unavailabilities', body).then(withCalendarFields)

export const deleteUnavailability = (id: string) =>
  apiClient.delete<{ success: boolean }>(`/api/planning/unavailabilities/${id}`)

// ── Events ──────────────────────────────────────────────────────────────────
export interface CreateEventBody extends SlotStartBody {
  kind: ScheduleEventKind
  technicalSurveyId?: string | null
  installationId?: string | null
  projectId?: string | null
  durationUnit: DurationUnit
  durationValue: number
  assignedUserIds?: string[]
  /** Note of the slot. On a RDV / internal work it becomes the bar's title instead of « RDV ». */
  label?: string
}

export const createEvent = (body: CreateEventBody) =>
  apiClient.post<ScheduleEventDto>('/api/planning/events', body).then(withCalendarFields)

export const rescheduleEvent = (id: string, body: { durationUnit: DurationUnit; durationValue: number; assignedUserIds?: string[]; reason?: string; label?: string } & SlotStartBody) =>
  apiClient.patch<ScheduleEventDto>(`/api/planning/events/${id}/reschedule`, body).then(withCalendarFields)

/**
 * Sets (or clears) the free note of ONE slot — SOLY-PLAN-004.
 *
 * Its own endpoint, not a field of the reschedule: writing a note must not void the client
 * confirmation (which a start change does) nor look like a plan change in the history.
 */
export const setEventNote = (id: string, note: string | null) =>
  apiClient.patch<ScheduleEventDto>(`/api/planning/events/${id}/note`, { note }).then(withCalendarFields)

export const cancelEvent = (id: string, reason: string) =>
  apiClient.post<ScheduleEventDto>(`/api/planning/events/${id}/cancel`, { reason }).then(withCalendarFields)

export const assignEvent = (id: string, assignedUserId: string) =>
  apiClient.post<ScheduleEventDto>(`/api/planning/events/${id}/assignments`, { assignedUserId }).then(withCalendarFields)

export const unassignEvent = (id: string, assignedUserId: string) =>
  apiClient.delete<ScheduleEventDto>(`/api/planning/events/${id}/assignments/${assignedUserId}`).then(withCalendarFields)

// ── Project detail + work-item actions ──────────────────────────────────────
export const getProjectDetail = (projectId: string) =>
  apiClient.get<ProjectDetailDto>(`/api/planning/projects/${projectId}`)
    .then((d) => ({ ...d, events: withCalendarFieldsAll(d.events) }))

// ── Second intervention (SOLY-FINITION-001 MS-03) ───────────────────────────
/**
 * Result of POST /interventions: the created ids + the nature (always Finition since
 * SOLY-PLAN-003) so the FE can render the badge without a re-fetch.
 */
export interface AddInterventionResultDto {
  installationId: string
  technicalSurveyId: string | null
  nature: InterventionNature
  phase: number
}

/**
 * Creates a second intervention: a new pose on the next phase (+ optional re-métré). No nature in
 * the body BY DESIGN — it is derived server-side. Errors: 404 PROJECT_NOT_FOUND,
 * 400 INVALID_OPERATION (no prior pose InProgress/Completed), 400 INVALID_ARGUMENT.
 */
export const addIntervention = (projectId: string, body: { withSurvey: boolean; note?: string | null }) =>
  apiClient.post<AddInterventionResultDto>(`/api/planning/projects/${projectId}/interventions`, body)

export const surveyNotApplicable = (id: string, projectId: string, reason: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/technical-surveys/${id}/not-applicable`, { projectId, reason })

export const surveyCancel = (id: string, projectId: string, reason: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/technical-surveys/${id}/cancel`, { projectId, reason })

export const surveyReport = (id: string, projectId: string, summary: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/technical-surveys/${id}/report`, { projectId, summary })

/** Reverse a « non nécessaire » survey → back to NotScheduled (à planifier). */
export const surveyReactivate = (id: string, projectId: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/technical-surveys/${id}/reactivate`, { projectId })

export const installationNotApplicable = (id: string, projectId: string, reason: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/not-applicable`, { projectId, reason })

export const installationCancel = (id: string, projectId: string, reason: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/cancel`, { projectId, reason })

export const installationStart = (id: string, projectId: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/start`, { projectId })

/** Reverse a « non nécessaire » installation → back to NotScheduled (à planifier). */
export const installationReactivate = (id: string, projectId: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/reactivate`, { projectId })

export const installationReport = (id: string, projectId: string, summary: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/report`, { projectId, summary })

/**
 * Confirm a slot: Proposé → Confirmé. Whoever agreed to it — the client, the boss, a commercial
 * (SOLY-PLAN-002). The confirmation lives on the EVENT, so a reschedule to another date puts it
 * back to Proposé server-side.
 *
 * ⚠️ The route still reads `confirm-client` although nothing else does: renaming a URL would have
 * cost a route alias for the whole BE→FE deployment window, for a string no user ever reads.
 * Dated debt, also noted on the BE controller.
 */
export const confirmEvent = (eventId: string, channel: ConfirmationChannel, note?: string) =>
  apiClient.patch<ScheduleEventDto>(`/api/planning/events/${eventId}/confirm-client`, { channel, note }).then(withCalendarFields)

/** Confirmé → Proposé. Idempotent. Without it, undoing meant rescheduling — i.e. moving the date. */
export const unconfirmEvent = (eventId: string) =>
  apiClient.patch<ScheduleEventDto>(`/api/planning/events/${eventId}/unconfirm`, {}).then(withCalendarFields)

/** Reclassifies a slot between the two free kinds (RDV ↔ Interne) — SOLY-PLAN-002 MS-03. */
export const convertEventKind = (eventId: string, kind: ScheduleEventKind) =>
  apiClient.patch<ScheduleEventDto>(`/api/planning/events/${eventId}/kind`, { kind }).then(withCalendarFields)

/** Métré estimate (hours, step 0.5). Optional reason → PlanningAuditLog. */
export const surveyEstimate = (id: string, projectId: string, hours: number, reason?: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/technical-surveys/${id}/estimate`, { projectId, hours, reason })

/** Pose estimate (days OR hours × resources). Optional reason → PlanningAuditLog. */
export const installationEstimate = (
  id: string,
  projectId: string,
  body: { unit: DurationUnit; value: number; resources: number; reason?: string },
) => apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/estimate`, { projectId, ...body })

/** Scheduling notes (SchedulingNotes) on the installation. */
export const installationNotes = (id: string, projectId: string, notes: string) =>
  apiClient.patch<{ success: boolean }>(`/api/planning/installations/${id}/notes`, { projectId, notes })

// ── Supplier order lines (MS-12, reshaped by SOLY-PO-001 MS-02) ─────────────
// The per-date legacy mutations (ordered/confirmed/received) were removed: those facts
// belong to the ORDER lifecycle below — single write path into the state machine.
export const getSupplierLines = (projectId: string) =>
  apiClient.get<SupplierOrderLineDto[]>(`/api/planning/supplier-order-lines?projectId=${projectId}`)

export const createSupplierLine = (body: { projectId: string; description: string; sourceOrderLineId?: string | null; supplierId?: string | null }) =>
  apiClient.post<SupplierOrderLineDto>('/api/planning/supplier-order-lines', body)

// ── Suppliers (SOLY-PO-001 MS-01) ────────────────────────────────────────────
export const getSuppliers = () =>
  apiClient.get<SupplierDto[]>('/api/planning/suppliers')

export const createSupplier = (body: { name: string; allowedModes: SupplierOrderMode[]; expectsConfirmation: boolean; defaultConfirmationDelayDays?: number | null }) =>
  apiClient.post<SupplierDto>('/api/planning/suppliers', body)

export const updateSupplier = (id: string, body: { name: string; allowedModes: SupplierOrderMode[]; expectsConfirmation: boolean; defaultConfirmationDelayDays?: number | null }) =>
  apiClient.put<SupplierDto>(`/api/planning/suppliers/${id}`, body)

// ── SOLY-SUPPLIER-002 MS-01 — suppliers page: brands, addresses, platforms ───

export type EmailAddressKind = 'Address' | 'Domain'
export type SupplierDataSource = 'Manual' | 'EmailImport'
export type OrderPlatformMode = 'Tradeplace' | 'Portal'

export interface PurchaseBrandDto {
  id: string
  name: string
}

export interface SupplierEmailAddressDto {
  id: string
  /** Lower-case « name@domain.be », or « @domain.be » for a whole domain. */
  value: string
  kind: EmailAddressKind
  source: SupplierDataSource
}

export interface SupplierOverviewDto extends SupplierDto {
  /** The supplier's own brand first, then alphabetical (server order). */
  brands: PurchaseBrandDto[]
  emailAddresses: SupplierEmailAddressDto[]
  /** Orders sent or confirmed — the « En cours » filter of the orders board. */
  inProgressOrderCount: number
}

export interface BrandSupplierDto {
  id: string
  name: string
}

/** One row of the « by brand » view. `id` is null for a brand only seen in sales (no supplier yet). */
export interface BrandOverviewDto {
  id: string | null
  name: string
  suppliers: BrandSupplierDto[]
  soldLineCount: number
}

export interface OrderPlatformAddressDto {
  id: string
  mode: OrderPlatformMode
  value: string
  kind: EmailAddressKind
  source: SupplierDataSource
}

export const getSuppliersOverview = () =>
  apiClient.get<SupplierOverviewDto[]>('/api/planning/suppliers/overview')

export const getSupplierOverview = (id: string) =>
  apiClient.get<SupplierOverviewDto>(`/api/planning/suppliers/${id}`)

export const addSupplierEmailAddress = (supplierId: string, value: string) =>
  apiClient.post<SupplierEmailAddressDto>(`/api/planning/suppliers/${supplierId}/email-addresses`, { value })

export const removeSupplierEmailAddress = (supplierId: string, addressId: string) =>
  apiClient.delete<void>(`/api/planning/suppliers/${supplierId}/email-addresses/${addressId}`)

export const addSupplierBrand = (supplierId: string, name: string) =>
  apiClient.post<PurchaseBrandDto>(`/api/planning/suppliers/${supplierId}/brands`, { name })

export const removeSupplierBrand = (supplierId: string, brandId: string) =>
  apiClient.delete<void>(`/api/planning/suppliers/${supplierId}/brands/${brandId}`)

export const getPurchaseBrands = () =>
  apiClient.get<BrandOverviewDto[]>('/api/planning/purchase-brands')

export const renamePurchaseBrand = (brandId: string, name: string) =>
  apiClient.put<PurchaseBrandDto>(`/api/planning/purchase-brands/${brandId}`, { name })

export const getOrderPlatforms = () =>
  apiClient.get<OrderPlatformAddressDto[]>('/api/planning/order-platforms')

export const addOrderPlatform = (mode: OrderPlatformMode, value: string) =>
  apiClient.post<OrderPlatformAddressDto>('/api/planning/order-platforms', { mode, value })

export const removeOrderPlatform = (addressId: string) =>
  apiClient.delete<void>(`/api/planning/order-platforms/${addressId}`)

// ── SOLY-SUPPLIER-002 MS-02/MS-04 — Outlook mailboxes followed for supplier orders ──

export type MailboxKind = 'Own' | 'Shared'
export type MailConnectionStatus = 'Active' | 'NeedsReconnect'

export interface MailboxDto {
  id: string
  address: string
  kind: MailboxKind
  /** The Microsoft account the mailbox is read through (the address itself, or someone who opens it). */
  accountEmail: string
  declarantUserId: string
  declarantName: string
  /** Added by the caller: only they can reconnect it. */
  isMine: boolean
  status: MailConnectionStatus
  supplierImportedUntil: string | null
}

/** Add a mailbox by its address, or reconnect one the caller added. */
export type MailboxConnectRequest = { address: string } | { mailboxId: string }

/**
 * Checks the request before Microsoft (format, already followed, permission) and returns the
 * Microsoft sign-in URL the browser goes to.
 */
export const startMailboxConnect = (request: MailboxConnectRequest) =>
  apiClient.post<{ authorizeUrl: string }>('/api/planning/mailboxes/connect', request)

/** Back from Microsoft: follows (or reconnects) the mailbox named when the sign-in started. */
export const completeMailboxConnect = (state: string, code: string) =>
  apiClient.post<MailboxDto>('/api/planning/mailboxes/connect/complete', { state, code })

export const getMailboxes = () => apiClient.get<MailboxDto[]>('/api/planning/mailboxes')

/** SOLY-SUPPLIER-002 MS-05 — the address « Ajouter une boîte » starts with: the caller's own, unless public or already followed. */
export const getSuggestedMailboxAddress = () =>
  apiClient.get<{ address: string | null }>('/api/planning/mailboxes/suggested-address')

/** The mailbox's Microsoft organization and the page where its administrator authorizes Soly. */
export interface AdminConsentLinkDto {
  domain: string
  tenantId: string
  url: string
}

/** Refused (400) for an address that is not a Microsoft 365 mailbox: MAILBOX_PUBLIC_DOMAIN, MAILBOX_NOT_MICROSOFT. */
export const getAdminConsentLink = (address: string) =>
  apiClient.get<AdminConsentLinkDto>(`/api/planning/mailboxes/admin-consent-link?address=${encodeURIComponent(address)}`)

export const removeMailbox = (mailboxId: string) =>
  apiClient.delete<void>(`/api/planning/mailboxes/${mailboxId}`)

// ── Supplier import from email subjects (SOLY-SUPPLIER-002 MS-03, V144) ──────

export type SupplierImportStatus = 'Reading' | 'Classifying' | 'Ready' | 'Applied' | 'Failed' | 'Invalidated'
export type SupplierImportCandidateKind = 'NewSupplier' | 'Complement' | 'Platform'
export type SupplierImportLevel = 'Sure' | 'Unsure'

export interface SupplierImportCandidateDto {
  id: string
  kind: SupplierImportCandidateKind
  targetSupplierId: string | null
  targetSupplierName: string | null
  platformMode: OrderPlatformMode | null
  /** « @stone-atelier.be ». */
  domain: string
  /** The NEW addresses seen under the domain (all of them for a new supplier). */
  addresses: { address: string; sent: number; received: number }[]
  suggestedName: string | null
  level: SupplierImportLevel
  suggestedBrands: string[]
  evidence: { subject: string; sent: boolean }[]
  sentCount: number
  receivedCount: number
  lastSeenAt: string
}

export interface SupplierImportDto {
  id: string
  status: SupplierImportStatus
  createdDate: string
  /** Why it failed, or on a finished import the mailboxes that could not be read. */
  message: string | null
  progress: { step: 'reading' | 'classifying'; emailsScanned: number; batchesDone: number; batchesTotal: number } | null
  mailboxes: { address: string; periodFrom: string; periodTo: string }[]
  candidates: SupplierImportCandidateDto[]
}

export interface ApplySupplierImportLine {
  candidateId: string
  checked: boolean
  name: string | null
  /** null = « Nouveau fournisseur ». */
  targetSupplierId: string | null
  brands: string[] | null
}

export interface ApplySupplierImportResultDto {
  applied: boolean
  errors: { candidateId: string; message: string; suggestedTargetSupplierId: string | null }[]
  suppliersAdded: number
  suppliersCompleted: number
  platformsAdded: number
  ignored: number
}

export interface SupplierEmailIgnoreDto {
  id: string
  domain: string
  createdDate: string
}

export const startSupplierImport = () => apiClient.post<SupplierImportDto>('/api/planning/supplier-import', {})

export const getCurrentSupplierImport = () =>
  apiClient.get<{ import: SupplierImportDto | null }>('/api/planning/supplier-import/current')

/** One slice of reading or recognition (under a minute). */
export const advanceSupplierImport = (runId: string) =>
  apiClient.post<SupplierImportDto>(`/api/planning/supplier-import/${runId}/advance`, {})

export const applySupplierImport = (runId: string, lines: ApplySupplierImportLine[]) =>
  apiClient.post<ApplySupplierImportResultDto>(`/api/planning/supplier-import/${runId}/apply`, { lines })

export const getSupplierEmailIgnores = () => apiClient.get<SupplierEmailIgnoreDto[]>('/api/planning/supplier-import/ignores')

export const removeSupplierEmailIgnore = (ignoreId: string) =>
  apiClient.delete<void>(`/api/planning/supplier-import/ignores/${ignoreId}`)

// ── Supplier orders (SOLY-PO-001 MS-02, V102) ────────────────────────────────
export const createSupplierOrderDraft = (body: { supplierId: string; lineIds: string[] }) =>
  apiClient.post<SupplierOrderDto>('/api/planning/supplier-orders', body)

/** SOLY-PO-001 MS-16 — the org-wide read-only registre: every non-closed order of the org. */
export const getSupplierOrdersBoard = () =>
  apiClient.get<BoardOrderDto[]>('/api/planning/supplier-orders/board')

export const attachOrderLines = (orderId: string, lineIds: string[]) =>
  apiClient.post<SupplierOrderDto>(`/api/planning/supplier-orders/${orderId}/attach-lines`, { lineIds })

export const detachOrderLine = (orderId: string, lineId: string) =>
  apiClient.post<SupplierOrderDto>(`/api/planning/supplier-orders/${orderId}/detach-line`, { lineId })

export const deleteOrderDraft = (orderId: string) =>
  apiClient.delete<void>(`/api/planning/supplier-orders/${orderId}`)

/** Draft → Sent (two-step: attach lines, then mark sent). Stamps desiredPeriod on every attached line. */
export const markSentOrder = (orderId: string, body: { mode: SupplierOrderMode; desiredPeriod: PeriodDto; sentDate: string; confirmationExpectedBy?: string | null }) =>
  apiClient.post<SupplierOrderDto>(`/api/planning/supplier-orders/${orderId}/mark-sent`, body)

export const holdSupplierLine = (lineId: string, body: { kind: SupplierLineHoldKind; until: string; note: string }) =>
  apiClient.post<SupplierOrderLineDto>(`/api/planning/supplier-orders/lines/${lineId}/hold`, body)

export const postponeLineHold = (lineId: string, date: string) =>
  apiClient.post<SupplierOrderLineDto>(`/api/planning/supplier-orders/lines/${lineId}/postpone-hold`, { date })

export const releaseSupplierLine = (lineId: string) =>
  apiClient.post<SupplierOrderLineDto>(`/api/planning/supplier-orders/lines/${lineId}/release`, {})

export const receiveSupplierLine = (lineId: string, date: string) =>
  apiClient.post<SupplierOrderLineDto>(`/api/planning/supplier-orders/lines/${lineId}/receive`, { date })

export const cancelSupplierLine = (lineId: string, body: { date: string; reason: string }) =>
  apiClient.post<SupplierOrderLineDto>(`/api/planning/supplier-orders/lines/${lineId}/cancel`, body)

// ── Client order picker + « Rien à commander » (SOLY-PO-001 MS-03) ──────────
/** The nature of a client order line — drives the registre's kind-aware rendering. */
export type ClientOrderLineKind = 'product_item' | 'service_item' | 'discount' | 'surcharge' | 'note'

export interface ClientOrderLineDto {
  id: string
  title: string
  description: string | null
  category: string | null
  quantity: number | null
  /** What the line IS: service/note shown read-only when not commandable; discount/surcharge hidden. */
  kind: ClientOrderLineKind
  /**
   * SOLY-IMPORT-003 S11 — the server's purchase rule: the line is set to « Acheter », or is a product
   * nobody qualified yet. Absent from an API older than S11.
   */
  commandable?: boolean
}

export interface ClientOrderDto {
  salesOrderId: string | null
  orderLines: ClientOrderLineDto[]
  /** The « rien à commander » marker — carried here so the picker never needs the heavy cockpit detail. */
  nothingToOrderAt: string | null
}

export const getClientOrder = (projectId: string) =>
  apiClient.get<ClientOrderDto>(`/api/planning/projects/${projectId}/client-order`)

// ── Activation on existing sold projects (SOLY-PLAN-001 MS-33/34) ───────────
/**
 * Activates site tracking on a project sold before Features:Planning was enabled — provisions
 * the phase-1 métré + pose (204, idempotent, existing execution-details fields preserved).
 * Errors: 404 PROJECT_NOT_FOUND, 422 NO_ACCEPTED_ORDER, 409 PROJECT_INACTIVE.
 */
export const initializeProjectPlanning = (projectId: string) =>
  apiClient.post<void>(`/api/planning/projects/${projectId}/initialize`, {})

export const markNothingToOrder = (projectId: string) =>
  apiClient.post<{ success: boolean }>(`/api/planning/projects/${projectId}/nothing-to-order`, {})

export const clearNothingToOrder = (projectId: string) =>
  apiClient.delete<{ success: boolean }>(`/api/planning/projects/${projectId}/nothing-to-order`)

/** A staged free-line add for the reconcile call. Title and description stay separate columns. */
export interface ReconcileFreeLine {
  description: string
  supplierId: string | null
  /** Quantity to order (MS-14) — optional, null when not specified. */
  quantity?: number | null
  /** Short product name. Separate from `description` since V108 — never folded into it. */
  title?: string | null
}

/**
 * Server-side column sizes for a supplier order line, mirrored from
 * `SupplierOrderLine.TitleMaxLength` / `DescriptionMaxLength` (themselves sized on the client order
 * line these copy from). The server clamps beyond this; the UI bounds the input so the user sees
 * the limit instead of silently losing the tail.
 */
export const SUPPLIER_LINE_TITLE_MAX = 500
export const SUPPLIER_LINE_DESCRIPTION_MAX = 2000

/**
 * The staged registre diff (SOLY-PO-001 — registre rework): one atomic write.
 *   - `adds`: client lines to start tracking (by sourceOrderLineId + title/description snapshot).
 *   - `removeLineIds`: tracked supplier lines to drop (only removable — ToOrder/OnHold — server-guarded).
 *   - `freeLines`: brand-new free lines.
 * 400 `{ error: { code: 'LINE_NOT_REMOVABLE' } }` if a non-removable line is in `removeLineIds`.
 */
export interface ReconcileClientOrderBody {
  adds: { sourceOrderLineId: string; description: string; title?: string | null; quantity?: number | null }[]
  removeLineIds: string[]
  freeLines: ReconcileFreeLine[]
}

export const reconcileClientOrderLines = (projectId: string, body: ReconcileClientOrderBody) =>
  apiClient.post<{ success: boolean }>(`/api/planning/projects/${projectId}/client-order/reconcile`, body)

/**
 * SOLY-PO-001 MS-14 — « Quincaillerie détectée ». A distinct handle spec found across the client
 * order's furniture (custom per-line + project default), grouped by model+color. A SUGGESTION:
 * `suggestedQuantity` is only a hint (sum of source furniture-line quantities — handle counts are
 * not stored), `fromProjectDefault` true when the model came from the project's default finish.
 */
export interface HardwareSuggestionDto {
  model: string
  color: string
  sourceLineCount: number
  suggestedQuantity: number | null
  fromProjectDefault: boolean
}

export const getHardwareSuggestions = (projectId: string) =>
  apiClient.get<HardwareSuggestionDto[]>(`/api/planning/projects/${projectId}/hardware-suggestions`)

// ── Reminders + follow-up thread (SOLY-PO-001 MS-04) ─────────────────────────
export interface PlanningReminderDto {
  id: string
  projectId: string | null
  supplierOrderId: string | null
  supplierOrderLineId: string | null
  /** The ACTION, imperative (« Relancer Van Hoecke »). */
  title: string
  description: string | null
  dueDate: string
  completedAt: string | null
  chainIndex: number
}

export interface SupplierOrderEntryDto {
  id: string
  occurredAt: string
  actorUserId: string
  kind: string
  payload: string | null
}

export const getReminders = (projectId: string) =>
  apiClient.get<PlanningReminderDto[]>(`/api/planning/reminders?projectId=${projectId}`)

export const createReminder = (body: { title: string; description?: string | null; dueDate: string; projectId?: string | null; supplierOrderId?: string | null; supplierOrderLineId?: string | null; chainIndex?: number }) =>
  apiClient.post<PlanningReminderDto>('/api/planning/reminders', body)

export const completeReminder = (id: string) =>
  apiClient.post<PlanningReminderDto>(`/api/planning/reminders/${id}/complete`, {})

export const rescheduleReminder = (id: string, date: string) =>
  apiClient.post<PlanningReminderDto>(`/api/planning/reminders/${id}/reschedule`, { date })

export const getOrderEntries = (orderId: string) =>
  apiClient.get<SupplierOrderEntryDto[]>(`/api/planning/supplier-orders/${orderId}/entries`)

export const commentOrder = (orderId: string, body: string) =>
  apiClient.post<{ success: boolean }>(`/api/planning/supplier-orders/${orderId}/comments`, { body })

// ── Free lines for the « Commandé » grouping panel (SOLY-PO-001 MS-12) ───────
export interface BoardLineDto {
  id: string
  projectId: string
  clientName: string
  description: string
  lineStatus: SupplierLineStatus
  supplierId: string | null
  supplierName: string | null
  holdKind: SupplierLineHoldKind | null
  holdUntil: string | null
  holdNote: string | null
  createdDate: string | null
  /** Short product name. Sent since the title/description split — a board line reads like any other. */
  title: string | null
}

// ── UX round 2 — drawer bulk gestures (SOLY-PO-001 MS-11/12) ─────────────────────

/** « Commandé » in one gesture: create + send. Stamps desiredPeriod on all lines. */
export const createAndSendOrder = (body: {
  supplierId: string
  lineIds: string[]
  mode: SupplierOrderMode
  desiredPeriod: PeriodDto
  sentDate: string
  confirmationExpectedBy?: string | null
}) => apiClient.post<SupplierOrderDto>('/api/planning/supplier-orders/create-and-send', body)

/** Bulk hold — grouper / info. All-or-nothing server-side. */
export const bulkHoldLines = (body: {
  lineIds: string[]
  kind: SupplierLineHoldKind
  until: string
  note?: string | null
  supplierId?: string | null
}) => apiClient.post<SupplierOrderLineDto[]>('/api/planning/supplier-orders/lines/bulk-hold', body)

/** Bulk confirm lines (Assigned → Confirmed): sets confirmedPeriod on each line and optionally stamps the order number. */
export const bulkConfirmLines = (body: {
  lineIds: string[]
  confirmedPeriod: PeriodDto
  confirmedAt: string
  supplierOrderNumber?: string | null
}) => apiClient.post<SupplierOrderLineDto[]>('/api/planning/supplier-orders/lines/bulk-confirm', body)

/** Reschedule the confirmed period on already-Confirmed lines. */
export const bulkRescheduleLines = (body: {
  lineIds: string[]
  newPeriod: PeriodDto
}) => apiClient.post<SupplierOrderLineDto[]>('/api/planning/supplier-orders/lines/bulk-reschedule', body)

/** Free lines of OTHER projects intended for this supplier — the « Commandé » form's ⛓ panel. */
export const getGroupingCandidates = (supplierId: string, excludeProjectId: string) =>
  apiClient.get<BoardLineDto[]>(`/api/planning/supplier-orders/grouping-candidates?supplierId=${supplierId}&excludeProjectId=${excludeProjectId}`)

// ── Audit / history (MS-15) ─────────────────────────────────────────────────
export interface PlanningAuditEntryDto {
  id: string
  aggregateType: string
  aggregateId: string
  changeType: string
  byUserId: string
  at: string
  reason: string | null
  reasonTag: string | null
  fieldChanged: string | null
  beforeValue: string | null
  afterValue: string | null
  correlationId: string | null
}

/** Full planning history for a project (events + work-item mutations log under the Project aggregate). */
export const getProjectAudit = (projectId: string) =>
  apiClient.get<PlanningAuditEntryDto[]>(`/api/planning/audit?aggregateType=Project&aggregateId=${projectId}`)

// ── Constraints / délais (MS-25/26) ─────────────────────────────────────────
/** Lead-time rule keys (mirror the BE PlanningConstraintKey enum). */
export type PlanningConstraintKey =
  | 'SurveyLeadMaxDaysBeforePose'
  | 'SurveyLeadMaxDaysEarlyBeforePose'
  | 'SurveyorAssignLeadDays'
  | 'SurveyClientConfirmLeadDays'
  | 'InstallerAssignLeadDays'
  | 'PoseClientConfirmLeadDays'
  // SOLY-PO-001 MS-05 — commandes
  | 'OrderSendLeadDays'
  | 'SupplierConfirmDelayDays'
  | 'DeliveryBeforePoseLeadDays'
  | 'OrderTriageDelayDays'
/** Which work item the rule applies to (drives Métré vs Pose grouping). */
export type ConstraintWorkItemKind = 'TechnicalSurvey' | 'Installation'
/** Where the effective value comes from: org/global default, project override, or a dismissal. */
export type ConstraintSource = 'Default' | 'Override' | 'Dismissed'

export interface ProjectConstraintRuleDto {
  key: PlanningConstraintKey
  workItemKind: ConstraintWorkItemKind
  days: number
  source: ConstraintSource
  dismissed: boolean
}

export interface ProjectConstraintsDto {
  projectId: string
  rules: ProjectConstraintRuleDto[]
}

export const getProjectConstraints = (projectId: string) =>
  apiClient.get<ProjectConstraintsDto>(`/api/planning/projects/${projectId}/constraints`)

/**
 * Override a rule (`valueDays` set, `dismiss:false`) or dismiss it for this project
 * (`dismiss:true`, `valueDays` omitted). `reason` is required (min 5 chars, mirrors BE).
 */
export const putProjectConstraint = (
  projectId: string,
  key: PlanningConstraintKey,
  body: { valueDays?: number; dismiss: boolean; reason: string },
) => apiClient.put<{ success: boolean }>(`/api/planning/projects/${projectId}/constraints/${key}`, body)

/** Back to the org default for this rule (drops the project override / dismissal). */
export const deleteProjectConstraint = (projectId: string, key: PlanningConstraintKey) =>
  apiClient.delete<{ success: boolean }>(`/api/planning/projects/${projectId}/constraints/${key}`)

// ── Employees / skills ──────────────────────────────────────────────────────
export const getEmployees = () => apiClient.get<EmployeeDto[]>('/api/admin/employees')

export const addEmployeeSkill = (userId: string, skill: EmployeeSkillType) =>
  apiClient.post<{ success: boolean }>(`/api/admin/employees/${userId}/skills`, { skill })

export const removeEmployeeSkill = (userId: string, skill: EmployeeSkillType) =>
  apiClient.delete<{ success: boolean }>(`/api/admin/employees/${userId}/skills/${skill}`)
